import { HEAT_PUMP_EFFECT_RATIO } from '../../../helpers/energy/energy.models';
import { required } from '../../../helpers/function.helpers';
import { toPercent } from '../../../helpers/string.helpers';
import { IElementCategory } from '../../../models/element-categories.model';
import {
  ElementPropertyType,
  IFactoryProperty,
} from '../../../models/element-property.model';
import { ArrayItem } from '../../../models/type-helpers.model';
import { commonElementCategoryProperties } from '../common-element-category-properties.model';
import {
  boilerFuelProductsNameToIdMap,
  HEAT_DEMAND_RECORD_NAME,
  HEAT_PUMP_COP_DEFAULT_NAME,
  HeatingPropertyName,
  HeatingType,
  HeatSourceOptions,
  HeatTargetOptions,
  MAX_CAPACITY_EXPRESSION,
} from './heating.model';

const efficiencyProperty: IFactoryProperty = {
  name: HeatingPropertyName.Efficiency,
  displayName: 'Efficiency',
  type: ElementPropertyType.Expression,
  unit: '%',
  min: 0,
  max: 10,
  fallbackCount: `${HeatingPropertyName.Type} == "${HeatingType.Boiler}" ? 0.92 : 1`,
  description:
    'The efficiency of the heating prodution. 90% means that for every 1 kW of electricity we can produce 0.9 kW of heat.',
};

export const conditionalEfficiencyProperty: ArrayItem<
  IElementCategory['properties']
> = {
  conditions: {
    [HeatingPropertyName.Type]: [
      HeatingType.Boiler,
      HeatingType.DistrictHeating,
      HeatingType.DirectElectricHeating,
    ],
  },
  nodes: [efficiencyProperty],
};

/**
 * Properties specific to heat pumps
 */
const heatPumpProperties: ArrayItem<IElementCategory['properties']> = {
  conditions: {
    [HeatingPropertyName.Type]: HeatingType.HeatPump,
  },
  nodes: [
    {
      name: HeatingPropertyName.HeatSource,
      type: ElementPropertyType.Select,
      options: Object.values(HeatSourceOptions),
      indentation: 1,
      description: 'How the heat pump is powered',
    },
    {
      name: HeatingPropertyName.COP,
      displayName: 'COP',
      type: ElementPropertyType.Expression,
      unit: 'none',
      indentation: 1,
      fallbackCount: `${HEAT_PUMP_COP_DEFAULT_NAME}[${HeatingPropertyName.HeatSource}]`, // Typically between 2 and 5
      description:
        'Coefficient of Performance. The higher the COP, the more efficient the heat pump. 3 means that for every 1 kW of electricity, the heat pump can produce 3 kW of heat.',
    },
    {
      name: HeatingPropertyName.HeatPumpCapacityPercent,
      displayName: 'Pump share of max capacity',
      type: ElementPropertyType.Expression,
      unit: '%',
      min: 0,
      max: 1,
      indentation: 1,
      fallbackCount: `${toPercent(HEAT_PUMP_EFFECT_RATIO.effect)}`,
      description:
        'How much of the peak heating demand should be covered by the heat pump. The rest will be covered by auxiliary heating. Increasing this value would typically decrease COP.',
    },
  ],
};

const boilerFuelNames = Object.keys(boilerFuelProductsNameToIdMap);

/**
 * Properties specific to boilers
 */
const boilerProperties: ArrayItem<IElementCategory['properties']> = {
  conditions: {
    [HeatingPropertyName.Type]: HeatingType.Boiler,
  },
  nodes: [
    {
      name: HeatingPropertyName.FuelType,
      type: ElementPropertyType.Select,
      count: required(boilerFuelNames[0]),
      options: boilerFuelNames,
      indentation: 1,
    },
  ],
};

const tapwaterShareOfSpaceHeatingExpression = `${HEAT_DEMAND_RECORD_NAME}["${HeatTargetOptions.TapWater}"] / ${HEAT_DEMAND_RECORD_NAME}["${HeatTargetOptions.SpaceHeatingAndTapWater}"]`;

export const heatingProperties: IElementCategory['properties'] = [
  {
    name: HeatingPropertyName.MaxCapacity,
    type: ElementPropertyType.Expression,
    unit: 'kW',
    fallbackCount: MAX_CAPACITY_EXPRESSION,
    description: `The required heat production capacity to cover the coldest days of the year. 
        This is automatically calculated based on the energy demand and the building location.`,
  },
  {
    name: HeatingPropertyName.Type,
    type: ElementPropertyType.Select,
    options: Object.values(HeatingType),
  },
  {
    name: HeatingPropertyName.HeatTarget,
    type: ElementPropertyType.Select,
    options: Object.values(HeatTargetOptions),
    count: HeatTargetOptions.SpaceHeatingAndTapWater,
    description: 'What the the heat production is used for',
  },

  // Only used to calculate Primary Energy Value (PET/PEV). We need to know how much of the consumption is tapwater vs space heating
  {
    name: HeatingPropertyName.TapwaterShare,
    type: ElementPropertyType.Expression,
    unit: '%',
    hidden: true,
    fallbackCount: `${HeatingPropertyName.HeatTarget} == "${HeatTargetOptions.SpaceHeatingAndTapWater}" ? (${tapwaterShareOfSpaceHeatingExpression}) : (${HeatingPropertyName.HeatTarget} == "${HeatTargetOptions.TapWater}" ? 1 : 0)`,
    description: 'How much of the heating consumption is tapwater',
  },
  conditionalEfficiencyProperty,
  heatPumpProperties,
  boilerProperties,
  ...commonElementCategoryProperties,
  // {
  //   name: HeatingPropertyName.DistributionSystem,
  //   type: ElementPropertyType.Select,
  //   options: Object.values(DistributionSystemOptions),
  // },
];
